import { useState } from 'react';
import { Film, Mail, ArrowLeft, Loader2, CheckCircle2 } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { Button } from '../../components/atoms/Button';
import { Input } from '../../components/atoms/Input';
import { Card } from '../../components/atoms/Card';
import { Link } from 'react-router-dom';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { resetPassword, isLoading, error, clearError } = useAuthStore();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    clearError();
    await resetPassword(email);
    
    // Always show success message even if user doesn't exist (security)
    setIsSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-[#030303] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="p-3 bg-blue-600 rounded-xl shadow-lg shadow-blue-600/20">
            <Film size={28} className="text-white" />
          </div>
          <h1 className="text-2xl font-black tracking-tighter uppercase text-white">
            PRODUKCIJSKI <span className="text-blue-500">MASTER</span>
          </h1>
        </div>

        <Card variant="glass" padding="lg" radius="3xl">
          {/* Back Link */}
          <Link 
            to="/login" 
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-6 transition-colors"
          >
            <ArrowLeft size={16} />
            Back to Login
          </Link>

          <div className="text-center mb-6">
            <h2 className="text-lg font-bold text-white mb-1">Reset Password</h2>
            <p className="text-sm text-slate-400">
              Enter your email and we'll send you a link to reset your password
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm">
              {error}
            </div>
          )}

          {isSubmitted ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 size={32} className="text-emerald-500" />
              </div>
              <h3 className="text-white font-bold mb-2">Check your email</h3>
              <p className="text-slate-400 text-sm mb-4">
                If an account exists for <strong className="text-white">{email}</strong>, 
                you'll receive a password reset link shortly.
              </p>
              <p className="text-slate-500 text-xs">
                Don't see it? Check your spam folder or{' '}
                <button 
                  onClick={() => setIsSubmitted(false)} 
                  className="text-blue-500 hover:underline"
                >
                  try again
                </button>
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                leftIcon={<Mail size={16} />}
                required
              />
              <Button
                type="submit"
                variant="primary"
                className="w-full"
                isLoading={isLoading}
                disabled={!email}
              >
                Send Reset Link
              </Button>
            </form>
          )}
        </Card>

        <p className="text-center mt-6 text-xs text-slate-600">
          © 2024 ZADAR ARCHIPELAGO PRODUCTIONS
        </p>
      </div>
    </div>
  );
}
